import { sumBy } from 'lodash';
import { forEach, forOwn } from '../../utils';
import { VATUpdateAction, } from './types';
import { PricingMethod, } from './../types';
/**
 *  @internal Provide this constant as 4th param to
 *  the createPricingDetails method to add a single
 *  VAT object instead of a collection to the
 *  pricing details.
 */
const SINGLE_VAT = false;
/**
 *  @internal The default pricing properties in a
 *  pricing details object. Used to loop in the
 *  code below.
 */
const PRICING_DETAILS_DATA = [
    'base_price',
    'total_fees',
    'subtotal_price',
    'total_price',
];
/**
 *  All pricing logic. (!) Used for both the shop and the cart.
 */
export default {
    /**
     *  Used to create a pricing details object. A (global)
     *  product has one (self), but a ticket is built up by
     *  three: self, children own and children container.
     *
     *  (i) Details can either contain data of a single VAT
     *  (only applicable for the self) or a VAT collection,
     *  holding every applied VAT in it.
     */
    createPricingDetails(basePrice = 0, vatPercentage = 0, VATCollection = true) {
        // Both type of details hold the default pricing details
        const details = {
            base_price: basePrice,
            total_fees: 0,
            subtotal_price: basePrice,
            total_price: basePrice,
        };
        if (VATCollection) {
            details.including_vats = {
                percentages: {},
                total: 0,
            };
        }
        else {
            details.vat = {
                percentage: vatPercentage,
                amount: 0,
            };
        }
        return details;
    },
    /**
     *  Used to create a default pricing object.
     *  (i) Used for (global) products and as base for tickets
     */
    createPricing(basePrice, fees, vatPercentage = 0) {
        const pricing = this.createPricingDetails(basePrice, vatPercentage, SINGLE_VAT);
        return Object.assign(Object.assign({ fees }, pricing), { coupons: {} });
    },
    /**
     *  Used to create a ticket's pricing object, which contains more
     *  than a product's pricing because the pricing of a ticket is
     *  computed based on multiple pricing details.
     */
    createTicketPricing(basePrice, fees, vatPercentage = 0) {
        // Start with a default pricing object
        const pricing = this.createPricing(basePrice, fees, vatPercentage);
        /**
         *  Extend the pricing with a self which holds the pricing details of himself,
         *  because the total computed pricing details are saved top level. Also extend
         *  it with a collection for the including VATS and the coupon counts and an
         *  object that holds the pricing details of his children.
         */
        pricing.self = this.createPricingDetails(basePrice, vatPercentage, SINGLE_VAT);
        pricing.including_vats = {
            percentages: {},
            total: 0,
        };
        pricing.coupons_count = {};
        pricing.children = {
            own: this.createPricingDetails(),
            container: this.createPricingDetails(),
        };
        return pricing;
    },
    /**
     *
     *  Used to compute the pricing of a ticket. Based on required and/or
     *  optional products and taking his min price and applied coupons into
     *  account. Also builds the VAT collection based on the products that
     *  are used for the ticket and setting the highest VAT amount of his
     *  most expensive required product (if is has a value) as his own.
     *
     *  (!) The VAT amounts of the (ticket) products of a ticket must be
     *  computed before executing this method, as the fees and VATs of
     *  products are used for computation.
     *
     */
    computeTicketPricing(ticketPricing, ticketMinPrice, products, ignoreOptional = false) {
        // Reset the pricing to always build up the pricing from zeros
        this.resetTicketPricing(ticketPricing);
        // Subdivide all the products of the ticket in optional
        // and required for more easy further processing
        let ticketProducts = {
            all: products,
            optional: [],
            required: [],
        };
        forEach(products, (product) => {
            ticketProducts[product.is_optional ? 'optional' : 'required'].push(product);
        });
        // Use the min price of the ticket if provided
        ticketPricing.self.base_price = ticketMinPrice || 0;
        // Save container products for adding their pricing last, as
        // they apply on the ticket's base price in its entirety
        const containerProducts = [];
        // Fn for adding a (ticket) product to the pricing of the ticket
        const addProductToPricing = (product) => {
            const productPricing = product.pricing;
            /**
             *  The pricing and the fees of optional products are
             *  added to the children's pricing because they are
             *  separate from the ticket, as where required products
             *  actually become the ticket pricing
             */
            if (product.is_optional) {
                ticketPricing.children.own.base_price += productPricing.base_price;
                ticketPricing.children.own.total_fees += productPricing.total_fees;
                ticketPricing.children.own.subtotal_price += productPricing.subtotal_price;
                ticketPricing.children.own.total_price += productPricing.total_price;
                this.updateVATCollection(VATUpdateAction.Add, ticketPricing.children.own.including_vats, productPricing.vat.percentage, productPricing.vat.amount);
                return;
            }
            // Required product
            if (productPricing.base_price > 0) {
                // The sum of the base and sub prices of required
                // products become the prices of the ticket itself
                ticketPricing.self.base_price += productPricing.base_price;
                // The ticket's VAT percentage becomes equal to the VAT percentage
                // of his most expensive required product (that has a value)
                ticketPricing.self.vat.percentage = Math.max(ticketPricing.self.vat.percentage, productPricing.vat.percentage);
            }
        };
        /**
         *  The base price of a ticket should only be computed based on his
         *  required products if the API did not send back a min price.
         *
         *  Coupons are applied on product level, so to also let coupons apply
         *  on tickets with a min price returned by the API we should subtract
         *  the coupon discounts of the ticket's min price.
         */
        if (typeof ticketMinPrice !== 'number') {
            // The ticket's VAT percentage becomes equal to the VAT percentage
            // of his most expensive required product (that has a value) in
            // the addProductToPricing method above
            ticketPricing.self.vat.percentage = 0;
            forEach(ticketProducts.required, (ticketProduct) => {
                if (ticketProduct.item.pricing_method === PricingMethod.Container) {
                    containerProducts.push(ticketProduct);
                    return;
                }
                addProductToPricing(ticketProduct);
            });
        }
        else if (ticketPricing.self.base_price > 0) {
            forEach(ticketProducts.required, (product) => {
                forOwn(product.pricing.coupons, (couponDelta) => {
                    ticketPricing.self.base_price -= couponDelta;
                    return ticketPricing.self.base_price > 0;
                });
                return ticketPricing.self.base_price > 0;
            });
            ticketPricing.self.base_price = Math.max(0, ticketPricing.self.base_price);
        }
        /**
         *  Compute the total fees based on the computed base price at this point
         *  or reset to zero because fees do not apply on free tickets.
         */
        if (ticketPricing.self.base_price > 0) {
            ticketPricing.self.total_fees += ticketPricing.fees.fixed;
            ticketPricing.self.total_fees += Math.round((ticketPricing.fees.percentage / 100) * ticketPricing.self.base_price);
        }
        else {
            ticketPricing.self.total_fees = 0;
        }
        /**
         *  Compute the total price, VAT amount and subtotal at this point, because
         *  the VAT amount should be computed based on either his out of the API
         *  returned min_price plus fees, or his computed min price (based on
         *  his required products) plus fees.
         */
        ticketPricing.self.total_price = ticketPricing.self.base_price + ticketPricing.self.total_fees;
        ticketPricing.self.vat.amount = Math.ceil((ticketPricing.self.total_price / (100 + ticketPricing.self.vat.percentage)) * ticketPricing.self.vat.percentage);
        ticketPricing.self.subtotal_price = ticketPricing.self.total_price - ticketPricing.self.vat.amount;
        // The price of the optional products is only added when not ignored
        if (!ignoreOptional) {
            forEach(ticketProducts.optional, (product) => {
                if (product.item.pricing_method === PricingMethod.Container) {
                    containerProducts.push(product);
                    return;
                }
                addProductToPricing(product);
            });
        }
        // Add the container products details last, because their pricing applies to the total ticket price
        if (containerProducts.length) {
            // Recompute the total prices of the product based on his container
            // price (the ticket) before adding his fees and VAT to the ticket
            // (i) It's ok if it becomes zero because the ticket price is zero
            const originalTicketPrice = ticketPricing.self.total_price + ticketPricing.children.own.total_price;
            forEach(containerProducts, (product) => {
                this.computeProductPricing(product.pricing, originalTicketPrice);
                // Only use the recomputed total fees, subtotal and total price of the product
                // (!) The base price is ignored for container products
                ticketPricing.children.container.total_fees += product.pricing.total_fees;
                ticketPricing.children.container.subtotal_price += product.pricing.subtotal_price;
                ticketPricing.children.container.total_price += product.pricing.total_price;
                this.updateVATCollection(VATUpdateAction.Add, ticketPricing.children.container.including_vats, product.pricing.vat.percentage, product.pricing.vat.amount);
            });
        }
        // Compute his totals based on the computed details
        this.computeTicketTotal(ticketPricing);
    },
    /**
     *  Used for resetting the pricing of a ticket, happening every time
     *  the pricing of a ticket should be computed to make sure the
     *  values are being computed from zero.
     */
    resetTicketPricing(ticketPricing) {
        forEach(PRICING_DETAILS_DATA, (dataProp) => {
            ticketPricing[dataProp] = 0;
        });
        ticketPricing.including_vats = {
            percentages: {},
            total: 0,
        };
        ticketPricing.self = this.createPricingDetails(ticketPricing.self.base_price, ticketPricing.self.vat.percentage, SINGLE_VAT);
        ticketPricing.children.own = this.createPricingDetails();
        ticketPricing.children.container = this.createPricingDetails();
    },
    /**
     *  Compute a ticket's total pricing by summing every pricing
     *  details (self, own and container children) of the ticket.
     */
    computeTicketTotal(ticketPricing) {
        const details = [
            ticketPricing.children.own,
            ticketPricing.children.container,
        ];
        // Add the details of himself to the ticket's totals
        forEach(PRICING_DETAILS_DATA, (dataProp) => {
            ticketPricing[dataProp] += ticketPricing.self[dataProp];
        });
        this.updateVATCollection(VATUpdateAction.Add, ticketPricing.including_vats, ticketPricing.self.vat.percentage, ticketPricing.self.vat.amount);
        // Add the details of the children to the ticket's totals
        forEach(details, (detail) => {
            forEach(PRICING_DETAILS_DATA, (dataProp) => {
                ticketPricing[dataProp] += detail[dataProp];
            });
            forOwn(detail.including_vats.percentages, (amount, percentage) => {
                this.updateVATCollection(VATUpdateAction.Add, ticketPricing.including_vats, percentage, amount);
            });
        });
    },
    /**
     *  Used to compute the total prices of a product's pricing object.
     */
    computeProductPricing(pricing, containerPrice) {
        // Use the container price for computation if forwarded
        const price = typeof containerPrice === 'number'
            ? containerPrice
            : pricing.base_price;
        // Ignore fees and reset totals to zero when the price is zero
        // (i) The VAT and fees do not apply on a free ticket, product or container item
        if (price === 0) {
            pricing.vat.amount = 0;
            pricing.base_price = 0;
            pricing.subtotal_price = 0;
            pricing.total_fees = 0;
            pricing.total_price = 0;
            return;
        }
        // Compute the total fees
        pricing.total_fees = pricing.fees.fixed;
        pricing.total_fees += Math.round((pricing.fees.percentage / 100) * price);
        // The VAT is already included in the base price
        pricing.total_price = pricing.base_price + pricing.total_fees;
        // The VAT amount should be subtracted out of the fees in case of a
        // container pricing and out of the total price by default
        const amountThatIncludesVAT = pricing[containerPrice ? 'total_fees' : 'total_price'];
        pricing.vat.amount = Math.ceil((amountThatIncludesVAT / (100 + pricing.vat.percentage)) * pricing.vat.percentage);
        // Compute the subtotal price
        pricing.subtotal_price = pricing.total_price - pricing.vat.amount;
    },
    /**
     *
     *  Used to compute the pricing of the forwarded products and tickets, as
     *  used for both regular products and tickets from the shop and cart product
     *  and cart tickets from the cart.
     *
     *  (i) As the location of the products of a ticket differs per type
     *  of ticket, a method for retrieving them must be forwarded.
     *
     */
    computeItemsPricing(products, tickets, getProductsOfTicket, ignoreOptional = false) {
        forOwn(products, (product) => {
            this.computeProductPricing(product.pricing);
        });
        forOwn(tickets, (ticket) => {
            this.computeTicketPricing(ticket.pricing, ticket.min_price, getProductsOfTicket(ticket), ignoreOptional);
        });
    },
    /**
     *  Calculate the cumulative pricing object for an array of cart items.
     */
    getCumulativeCartItemPrice(items) {
        const pricing = items.map((item) => item.pricing);
        return {
            base_price: sumBy(pricing, 'base_price'),
            total_fees: sumBy(pricing, 'total_fees'),
            subtotal_price: sumBy(pricing, 'subtotal_price'),
            total_price: sumBy(pricing, 'total_price'),
        };
    },
    /**
     *  Used to update the amount of a specific VAT percentage in a collection
     *  of VAT amounts. Meaning it simply adds or subtracts the forwarded amount
     *  to/from the right percentage key in the forwarded collection and
     *  adds/subtracts the amount of the total.
     */
    updateVATCollection(action, collection, percentage, amount) {
        const percentageKey = String(percentage);
        collection.percentages[percentageKey] = typeof collection.percentages[percentageKey] === 'number'
            ? collection.percentages[percentageKey]
            : 0;
        // Revert the amount if it's about subtracting
        amount = action === VATUpdateAction.Add ? amount : -amount;
        // Update the collection
        collection.percentages[percentageKey] += amount;
        collection.total += amount;
        // Remove the prop when there is no amount of VAT
        // applied for this percentage (anymore)
        if (collection.percentages[percentageKey] === 0) {
            delete collection.percentages[percentageKey];
        }
    },
};
//# sourceMappingURL=price.js.map